import { useLayoutEffect, useRef } from "react";

const DURATION_MS = 450;

/**
 * Tween a number into a span's text without re-rendering React each frame.
 * Returns the ref to put on the span. Pulses the span when the value changes.
 */
export default function useCountUp(
  value: number,
  format: (n: number) => string,
) {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const from = shown.current;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (from === null || from === value || reduce) {
      shown.current = value;
      el.textContent = format(value);
      return;
    }
    el.classList.remove("bill-amount-pulse");
    void el.offsetWidth; // restart the pulse animation
    el.classList.add("bill-amount-pulse");
    const start = performance.now();
    let frame = requestAnimationFrame(function tick(now) {
      const t = Math.min((now - start) / DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      shown.current = from + (value - from) * eased;
      el.textContent = format(Math.round(shown.current));
      if (t < 1) frame = requestAnimationFrame(tick);
      else shown.current = value;
    });
    return () => cancelAnimationFrame(frame);
  }, [value, format]);

  return ref;
}
